import { Link } from 'react-router-dom';
import { slugFromProductUrl } from '../data/mockCatalog';
import { useEffect, useMemo, useState } from 'react';
import { useCart } from '../state/cart';
import { useWishlist } from '../state/wishlist';
import { fetchCategories, fetchShopProducts } from '../api/catalog';

export function ShopPage() {
  const [products, setProducts] = useState([]);
  const [apiCategories, setApiCategories] = useState([]);
  const [catalogError, setCatalogError] = useState('');
  const { addItem } = useCart();
  const { toggleWishlist, isWishlisted } = useWishlist();
  const DEFAULT_STOCK = 20;
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [visibleCount, setVisibleCount] = useState(9);

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyProduct, setNotifyProduct] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyStatus, setNotifyStatus] = useState({ type: '', message: '' });

  const openNotify = (product, slug) => {
    setNotifyStatus({ type: '', message: '' });
    setNotifyProduct({
      slug: String(slug || '').trim(),
      name: String(product?.name || '').trim(),
      image: product?.image || null,
    });
    setNotifyEmail('');
    setNotifyOpen(true);
  };

  const closeNotify = () => {
    setNotifyOpen(false);
    setNotifyProduct(null);
    setNotifyEmail('');
    setNotifyStatus({ type: '', message: '' });
  };

  useEffect(() => {
    if (!notifyOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeNotify();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [notifyOpen]);

  const saveNotifyRequest = () => {
    const email = notifyEmail.trim();
    const slug = String(notifyProduct?.slug || '').trim();
    const name = String(notifyProduct?.name || '').trim();

    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!ok) {
      setNotifyStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }
    if (!slug) {
      setNotifyStatus({ type: 'error', message: 'Missing product information. Please try again.' });
      return;
    }

    try {
      const key = 'elenard_notify_requests';
      const raw = localStorage.getItem(key);
      const list = Array.isArray(JSON.parse(raw || '[]')) ? JSON.parse(raw || '[]') : [];
      const normalizedEmail = email.toLowerCase();
      const exists = list.some(
        (r) => String(r?.slug || '').toLowerCase() === slug.toLowerCase() && String(r?.email || '').toLowerCase() === normalizedEmail,
      );
      if (exists) {
        setNotifyStatus({ type: 'success', message: 'You are already subscribed for this product.' });
        return;
      }

      const next = [
        {
          slug,
          name,
          email,
          createdAt: new Date().toISOString(),
        },
        ...list,
      ].slice(0, 500);

      localStorage.setItem(key, JSON.stringify(next));
      setNotifyStatus({ type: 'success', message: 'Saved! We will notify you when it is back in stock.' });
    } catch (_) {
      setNotifyStatus({ type: 'error', message: 'Could not save your request in this browser.' });
    }
  };

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();

    (async () => {
      setCatalogError('');
      try {
        const [catResult, prodResult] = await Promise.allSettled([
          fetchCategories(ac.signal),
          fetchShopProducts(ac.signal),
        ]);
        if (cancelled) return;

        if (catResult.status === 'fulfilled' && Array.isArray(catResult.value) && catResult.value.length) {
          setApiCategories(catResult.value);
        }

        if (prodResult.status === 'fulfilled') {
          const apiProducts = prodResult.value?.products;
          if (Array.isArray(apiProducts) && apiProducts.length) {
            setProducts(apiProducts);
          }
        }

        const errs = [];
        if (catResult.status === 'rejected' && catResult.reason?.name !== 'AbortError') {
          errs.push(catResult.reason instanceof Error ? catResult.reason.message : String(catResult.reason));
        }
        if (prodResult.status === 'rejected' && prodResult.reason?.name !== 'AbortError') {
          errs.push(prodResult.reason instanceof Error ? prodResult.reason.message : String(prodResult.reason));
        }
        if (errs.length) {
          setCatalogError(errs.join(' · '));
        }
      } catch (e) {
        if (cancelled || e?.name === 'AbortError') return;
        setCatalogError(e instanceof Error ? e.message : 'Could not load catalog');
      } finally {
        // no-op
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, []);

  const slugNameMap = useMemo(() => {
    const m = new Map();
    apiCategories.forEach((c) => {
      const slug = c.slug || c.id;
      if (slug) m.set(String(slug), c.name || String(slug));
    });
    return m;
  }, [apiCategories]);

  const categoryOptions = useMemo(() => {
    const source = apiCategories.map((c) => ({
      id: String(c.slug || c.id || ''),
      name: c.name || String(c.slug || c.id),
    }));
    return [{ id: 'all', name: 'All categories' }, ...source.filter((o) => o.id)];
  }, [apiCategories]);

  const parsedProducts = useMemo(() => {
    const parsePrice = (priceText) => {
      const m = String(priceText || '').match(/[\d,.]+/);
      if (!m) return null;
      const n = Number(m[0].replace(/,/g, ''));
      return Number.isFinite(n) ? n : null;
    };

    return products.map((p) => {
      const fromApi = p.priceValue != null && Number.isFinite(Number(p.priceValue));
      const priceValue = fromApi ? Number(p.priceValue) : parsePrice(p.priceText);
      let matchedCategorySlug = p.category_slug ?? p.category?.slug ?? p.category?.id ?? null;
      if (!matchedCategorySlug && typeof p.category === 'string') {
        matchedCategorySlug = p.category;
      }

      return {
        ...p,
        priceValue,
        matchedCategorySlug,
      };
    });
  }, [products]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const min = minPrice.trim() === '' ? null : Number(minPrice);
    const max = maxPrice.trim() === '' ? null : Number(maxPrice);
    const hasMin = Number.isFinite(min);
    const hasMax = Number.isFinite(max);

    return parsedProducts.filter((p) => {
      if (inStockOnly && p.outOfStock) return false;
      if (category !== 'all') {
        if (p.matchedCategorySlug !== category) return false;
      }
      if (hasMin || hasMax) {
        if (!Number.isFinite(p.priceValue)) return false;
        if (hasMin && p.priceValue < min) return false;
        if (hasMax && p.priceValue > max) return false;
      }
      if (q) {
        const hay = `${p.name || ''} ${p.priceText || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [parsedProducts, query, category, inStockOnly, minPrice, maxPrice]);

  const pageItems = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  useEffect(() => {
    setVisibleCount(9);
  }, [query, category, inStockOnly, minPrice, maxPrice]);

  return (
    <section className="section-padding elenardSectionTight">
      <div className="container">
        <div className="row">
          <div className="col-lg-3 md-mb50">
            <div className="sec-head mb-30">
              <span className="sub-title main-color mb-10">Shop</span>
            </div>

            <div className="column">
              <div className="elenardShopSidebar">
                <div className="mb-30">
                  <label className="sub-title mb-10 d-block">Search</label>
                  <input
                    className="elenardShopInput"
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search products..."
                  />
                </div>

                <div className="mb-30">
                  <label className="sub-title mb-10 d-block">Price range</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <input
                      className="elenardShopInput"
                      inputMode="decimal"
                      value={minPrice}
                      onChange={(e) => setMinPrice(e.target.value)}
                      placeholder="Min"
                      aria-label="Minimum price"
                    />
                    <input
                      className="elenardShopInput"
                      inputMode="decimal"
                      value={maxPrice}
                      onChange={(e) => setMaxPrice(e.target.value)}
                      placeholder="Max"
                      aria-label="Maximum price"
                    />
                  </div>
                  <div className="mt-10" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="butn butn-sm butn-bord radius-30"
                      onClick={() => {
                        setMinPrice('');
                        setMaxPrice('');
                      }}
                    >
                      <span className="text">Clear</span>
                    </button>
                  </div>
                </div>

                <div className="mb-30">
                  <label className="sub-title mb-10 d-block">Category</label>
                  <select
                    className="elenardShopSelect"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {categoryOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-30">
                  <label className="elenardShopCheck">
                    <input
                      type="checkbox"
                      checked={inStockOnly}
                      onChange={(e) => setInStockOnly(e.target.checked)}
                    />
                    <span>In stock only</span>
                  </label>
                </div>

                <div className="pt-20 bord-thin-top">
                  <div className="opacity-8 fz-14">
                    Showing <b>{filtered.length}</b> product(s)
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-9">
            <div className="sec-head mb-30">
              <span className="sub-title main-color mb-10">Shop Online</span>
            </div>

            {catalogError ? (
              <div className="mb-20" style={{ color: 'rgba(255,255,255,0.7)' }}>
                <div style={{ color: '#fd5b38', fontWeight: 700, marginBottom: 6 }}>
                  Could not load products
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.85 }}>{catalogError}</div>
              </div>
            ) : null}

            <div className="row">
              {pageItems.map((p, idx) => {
                const slug = p.slug || slugFromProductUrl(p.url);
                const wid = slug ? `shop:${slug}` : '';
                const stockLeft = p.outOfStock
                  ? 0
                  : Number.isFinite(p.stock)
                    ? Math.max(0, p.stock)
                    : DEFAULT_STOCK;
                const maxQty = p.outOfStock ? 0 : stockLeft || DEFAULT_STOCK;
                const cartId =
                  p.numericId != null && Number(p.numericId) > 0
                    ? `product:${p.numericId}`
                    : slug
                      ? `shop:${slug}`
                      : '';
                const wishItem = slug
                  ? {
                      id: wid,
                      slug,
                      name: p.name,
                      price: Number(p.priceValue ?? 0),
                      priceText: p.priceText || null,
                      image: p.image,
                      outOfStock: !!p.outOfStock,
                    }
                  : null;
                const rowKey =
                  p.numericId != null ? `p-${p.numericId}` : `${p.url || 'row'}-${idx}`;
                return (
                  <div key={rowKey} className="col-lg-4 col-md-6 mb-30">
                    <div className="item elenardProductCard">
                      <div className="img position-relative">
                        {wishItem ? (
                          <button
                            type="button"
                            className={`elenardWishlistFab${isWishlisted(wid) ? ' elenardWishlistFab--on' : ''}`}
                            aria-label={
                              isWishlisted(wid) ? 'Remove from wishlist' : 'Add to wishlist'
                            }
                            aria-pressed={isWishlisted(wid)}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggleWishlist(wishItem);
                            }}
                          >
                            <i
                              className={isWishlisted(wid) ? 'fas fa-heart' : 'far fa-heart'}
                              aria-hidden="true"
                            />
                          </button>
                        ) : null}
                        <Link to={slug ? `/product/${slug}` : '#'}>
                          <img
                            src={p.image || '/assets/imgs/logo-light.svg'}
                            alt={p.name}
                            className="radius-15 elenardProductImg"
                            loading="lazy"
                          />
                        </Link>
                      </div>
                      <div className="cont mt-20 elenardProductCont">
                        <div className="elenardProductTop">
                          {p.badge ? (
                            <span className="elenardPill">{p.badge}</span>
                          ) : p.outOfStock ? (
                            <span className="elenardPill elenardPillMuted">Sold out</span>
                          ) : (
                            <span className="elenardPill">New!</span>
                          )}
                          {p.matchedCategorySlug ? (
                            <span className="elenardPill elenardPillMuted">
                              {slugNameMap.get(p.matchedCategorySlug) || p.matchedCategorySlug}
                            </span>
                          ) : null}
                        </div>

                        <h6 className="mb-10 elenardProductTitle">{p.name}</h6>

                        <div className="elenardPriceRow">
                          <div className="elenardPrice">{p.priceText || ''}</div>
                        </div>

                        <div className="opacity-8 fz-14 mt-10">
                          Stock left: <b>{stockLeft}</b>
                        </div>

                        <div
                          className="mt-15"
                          style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}
                        >
                          <Link
                            to={`/product/${slug || slugFromProductUrl(p.url)}`}
                            className="butn butn-sm butn-bord radius-30"
                          >
                            <span className="text">Read More</span>
                          </Link>
                          {p.outOfStock ? (
                            <button
                              type="button"
                              className="butn butn-sm butn-bord radius-30"
                              onClick={() => openNotify(p, slug)}
                            >
                              <span className="text">Notify me</span>
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className="butn butn-sm butn-bg main-colorbg radius-30"
                            disabled={p.outOfStock || !cartId}
                            onClick={() => {
                              if (!cartId || !slug) return;
                              const price = Number(p.priceValue ?? 0);
                              addItem({
                                id: cartId,
                                slug,
                                name: p.name,
                                price,
                                qty: 1,
                                image: p.image,
                                maxQty,
                              });
                            }}
                          >
                            <span className="text">
                              {p.outOfStock ? 'Sold out' : 'Add to Cart'}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filtered.length === 0 ? (
                <div className="col-12">
                  <p className="opacity-8">
                    No results found. Try adjusting search/filters.
                  </p>
                </div>
              ) : null}
            </div>

            {hasMore && (
              <div className="elenardViewMoreWrap">
                <button
                  type="button"
                  className="elenardViewMoreBtn"
                  onClick={() => setVisibleCount((n) => n + 9)}
                >
                  View More
                  <span className="elenardViewMoreCount">
                    ({filtered.length - visibleCount} remaining)
                  </span>
                </button>
              </div>
            )}

            {notifyOpen ? (
              <div
                className="elenardModal"
                role="dialog"
                aria-modal="true"
                aria-label="Notify when back in stock"
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) closeNotify();
                }}
              >
                <div className="elenardModalCard" role="document">
                  <div className="elenardModalHeader">
                    <div>
                      <div className="elenardModalTitle">Get notified</div>
                      <div className="elenardModalSubtitle">We’ll email you when this item is back in stock.</div>
                    </div>
                    <button type="button" className="elenardModalClose" aria-label="Close" onClick={closeNotify}>
                      ×
                    </button>
                  </div>

                  <div className="elenardModalBody">
                    <div className="elenardModalProduct">
                      {notifyProduct?.image ? (
                        <img className="elenardModalThumb" src={notifyProduct.image} alt="" />
                      ) : (
                        <div className="elenardModalThumb elenardModalThumb--empty" aria-hidden="true" />
                      )}
                      <div className="elenardModalProductMeta">
                        <div className="elenardModalProductName">{notifyProduct?.name || 'Selected item'}</div>
                        <div className="elenardModalProductHint">Sold out</div>
                      </div>
                    </div>

                    <label className="elenardModalLabel" htmlFor="elenardNotifyEmail">
                      Email address
                    </label>
                    <input
                      id="elenardNotifyEmail"
                      className="elenardModalInput"
                      type="email"
                      value={notifyEmail}
                      onChange={(e) => setNotifyEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoFocus
                    />

                    {notifyStatus?.message ? (
                      <div
                        className={`elenardModalMessage${notifyStatus.type === 'error' ? ' elenardModalMessage--error' : ' elenardModalMessage--success'}`}
                      >
                        {notifyStatus.message}
                      </div>
                    ) : null}
                  </div>

                  <div className="elenardModalFooter">
                    <button type="button" className="butn butn-sm butn-bord radius-30" onClick={closeNotify}>
                      <span className="text">Cancel</span>
                    </button>
                    <button type="button" className="butn butn-sm butn-bg main-colorbg radius-30" onClick={saveNotifyRequest}>
                      <span className="text">Notify me</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
